import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  StatusChip,
  toast,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { normalizeStatusPackScopeType } from './statusPackLinks.js';

function fmtDate(value) {
  if (!value) return 'No expiration set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function FieldTable({ fields = [] }) {
  if (!fields.length) {
    return (
      <EmptyState
        title="No approved public fields"
        description="This status pack has not approved any configured fields for public sharing yet."
      />
    );
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Field</th>
          <th>Approved public value</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={field.fieldKey}>
            <td>{field.label}</td>
            <td>{field.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ArtifactList({ artifacts = [] }) {
  if (!artifacts.length) {
    return (
      <EmptyState
        title="No approved artifacts"
        description="Approved public artifacts will appear here when a share-ready pack is assembled."
      />
    );
  }

  return (
    <div className="briefing-artifact-chip-row">
      {artifacts.map((artifact) => (
        <span key={artifact.id} className="chip outline sm">
          {artifact.title || artifact.id}
        </span>
      ))}
    </div>
  );
}

export function StatusPackLibraryPage() {
  const [searchParams] = useSearchParams();
  const requestedScopeType = normalizeStatusPackScopeType(searchParams.get('scopeType'));
  const requestedScopeId = String(searchParams.get('scopeId') || '').trim();
  const requestedScopeLabel = String(searchParams.get('scopeLabel') || '').trim();
  const requestedTitle = String(searchParams.get('title') || '').trim();
  const requestedSummary = String(searchParams.get('summary') || '').trim();
  const scopedListQuery = requestedScopeId
    ? { scopeType: requestedScopeType, scopeId: requestedScopeId, limit: 100 }
    : undefined;

  usePageMetadata({
    title: 'Status Pack Library',
    subtitle: 'Publish sanitized, read-only stakeholder status packs from approved PRIME records.',
  });
  const statusPacksRes = useApi(
    '/status-packs',
    scopedListQuery ? { query: scopedListQuery } : undefined,
  );
  const projectsRes = useApi('/projects', { query: { limit: 100 } });
  const programsRes = useApi('/programs', { query: { limit: 100 } });
  const portfoliosRes = useApi('/portfolios', { query: { limit: 100 } });
  const createMut = useMutation();
  const [draft, setDraft] = useState(() => ({
    scopeType: requestedScopeType,
    scopeId: requestedScopeId,
    title: requestedTitle,
    summary: requestedSummary,
  }));
  const packs = useMemo(() => statusPacksRes.data?.data || [], [statusPacksRes.data]);
  const projectOptions = useMemo(() => projectsRes.data?.data || [], [projectsRes.data]);
  const programOptions = useMemo(() => programsRes.data?.data || [], [programsRes.data]);
  const portfolioOptions = useMemo(() => portfoliosRes.data?.data || [], [portfoliosRes.data]);

  const baseScopeOptions = useMemo(
    () =>
      draft.scopeType === 'program'
        ? programOptions
        : draft.scopeType === 'portfolio'
          ? portfolioOptions
          : projectOptions,
    [draft.scopeType, programOptions, portfolioOptions, projectOptions],
  );
  const scopeOptions = useMemo(() => {
    if (!draft.scopeId || baseScopeOptions.some((option) => option.id === draft.scopeId)) {
      return baseScopeOptions;
    }
    return [
      {
        id: draft.scopeId,
        name: requestedScopeLabel || draft.scopeId,
      },
      ...baseScopeOptions,
    ];
  }, [baseScopeOptions, draft.scopeId, requestedScopeLabel]);

  useEffect(() => {
    if (draft.scopeId && scopeOptions.some((option) => option.id === draft.scopeId)) return;
    const nextScopeId = scopeOptions[0]?.id || '';
    if (nextScopeId === draft.scopeId) return;
    setDraft((current) => ({
      ...current,
      scopeId: nextScopeId,
    }));
  }, [draft.scopeId, scopeOptions]);

  async function createStatusPack() {
    try {
      await createMut.run(() =>
        api.post('/status-packs', {
          scopeType: draft.scopeType,
          scopeId: draft.scopeId,
          title: draft.title.trim(),
          summary: draft.summary.trim(),
        }),
      );
      toast.success({
        title: 'Status pack created',
        body: 'The draft stakeholder status pack is ready for review and publishing.',
      });
      setDraft((current) => ({ ...current, title: '', summary: '' }));
      await statusPacksRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Create failed', body: error.message });
    }
  }

  if (
    statusPacksRes.loading ||
    projectsRes.loading ||
    programsRes.loading ||
    portfoliosRes.loading
  ) {
    return <LoadingSkeleton rows={5} />;
  }
  if (statusPacksRes.error) return <ErrorState error={statusPacksRes.error} />;
  if (projectsRes.error) return <ErrorState error={projectsRes.error} />;
  if (programsRes.error) return <ErrorState error={programsRes.error} />;
  if (portfoliosRes.error) return <ErrorState error={portfoliosRes.error} />;

  return (
    <>
      <PageHeader
        eyebrow="Stakeholder Sharing"
        title="Status Pack Library"
        subtitle="Review draft packs, publish approved shares, and open the read-only stakeholder portal."
      />
      <section className="bento" style={{ marginTop: 'var(--space-3)' }}>
        <article className="span-12 card">
          <div className="eyebrow">Create stakeholder pack</div>
          <h2 className="card-title">Build a governed read-only status pack</h2>
          <p className="muted small">
            Generate a sanitized stakeholder pack from a governed project, program, or portfolio
            scope before human review and publishing.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginTop: 16,
            }}
          >
            <label className="form-field">
              <span className="label">Scope type</span>
              <select
                className="input"
                value={draft.scopeType}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    scopeType: event.target.value,
                    scopeId: '',
                  }))
                }
              >
                <option value="project">Project</option>
                <option value="program">Program</option>
                <option value="portfolio">Portfolio</option>
              </select>
            </label>
            <label className="form-field">
              <span className="label">Scope record</span>
              <select
                className="input"
                value={draft.scopeId}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    scopeId: event.target.value,
                  }))
                }
              >
                {scopeOptions.length ? null : <option value="">No records available</option>}
                {scopeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name || option.title || option.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="label">Title</span>
              <input
                className="input"
                value={draft.title}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
              />
            </label>
            <label className="form-field">
              <span className="label">Summary</span>
              <input
                className="input"
                value={draft.summary}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    summary: event.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="row" style={{ marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
            <button
              className="btn primary sm tactile-press"
              type="button"
              onClick={createStatusPack}
              disabled={createMut.running || !draft.scopeId}
            >
              {createMut.running ? 'Creating…' : 'Create status pack'}
            </button>
          </div>
        </article>
      </section>
      {packs.length === 0 ? (
        <EmptyState
          title={
            requestedScopeId ? 'No status packs for this workspace yet' : 'No status packs yet'
          }
          description={
            requestedScopeId
              ? 'Create a stakeholder status pack from this scoped workspace to begin controlled public sharing.'
              : 'Create a stakeholder status pack from an approved project, program, or portfolio to begin controlled public sharing.'
          }
        />
      ) : (
        <section className="bento" style={{ marginTop: 'var(--space-3)' }}>
          {packs.map((pack) => (
            <article key={pack.id} className="span-6 card">
              <div className="row spread">
                <div>
                  <div className="eyebrow">Status pack</div>
                  <h2 className="card-title" style={{ marginBottom: 6 }}>
                    {pack.title}
                  </h2>
                </div>
                <StatusChip status={pack.status || 'draft'} />
              </div>
              <p className="muted small">
                {pack.scopeType || 'scope'} · {pack.scopeId || 'pending scope'}
              </p>
              {pack.shareId ? (
                <p className="mono small" style={{ marginTop: 10 }}>
                  Share token: {pack.shareId}
                </p>
              ) : (
                <p className="muted small" style={{ marginTop: 10 }}>
                  Draft only — no public share link yet.
                </p>
              )}
              <div className="row" style={{ gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                <Link className="btn outline sm" to={`/status-packs/${pack.id}`}>
                  Open detail
                </Link>
                {pack.shareId ? (
                  <Link className="btn primary sm tactile-press" to={`/status/${pack.shareId}`}>
                    Open published view
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </section>
      )}
    </>
  );
}

// Convert the date-picker value (YYYY-MM-DD) into the ISO timestamp the
// publish endpoint stores as the share expiry.
function expiryDateToIso(dateValue) {
  if (!dateValue) return null;
  const date = new Date(`${dateValue}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function StatusPackDetailPage() {
  const { id } = useParams();
  // Expiry is picked with a date control and stays editable before publish
  // (FS-SHARE-2 / AC-SHARE-2.1).
  const [expiryDate, setExpiryDate] = useState('2026-12-31');
  // Which state-changing action awaits explicit confirmation:
  // 'publish' | 'revoke' | 'rotate' | null.
  const [pendingAction, setPendingAction] = useState(null);
  const packRes = useApi(`/status-packs/${id}`);
  const publishMut = useMutation();

  const pack = packRes.data?.data || null;

  usePageMetadata({
    title: pack?.title || 'Status Pack Detail',
    subtitle:
      'Review approved public fields, artifact scope, and publish read-only stakeholder access.',
  });

  async function publishPack() {
    try {
      await publishMut.run(() =>
        api.post(`/status-packs/${id}/publish`, {
          shareExpiresAt: expiryDateToIso(expiryDate),
        }),
      );
      setPendingAction(null);
      toast.success({
        title: 'Status pack published',
        body: 'Read-only stakeholder access is now available.',
      });
      await packRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Publish failed', body: error.message });
    }
  }

  // AC-SHARE-2.1: revoke kills the public link immediately; the confirm
  // captures the reviewer's feedback and the server audits the actor.
  async function revokePack(note) {
    try {
      await publishMut.run(() =>
        api.post(`/status-packs/${id}/revoke`, {
          note,
        }),
      );
      setPendingAction(null);
      toast.success({
        title: 'Share revoked',
        body: 'The public status link no longer resolves; stakeholders see the revocation notice.',
      });
      await packRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Revoke failed', body: error.message });
    }
  }

  async function rotateShare() {
    try {
      await publishMut.run(() => api.post(`/status-packs/${id}/rotate-share`, {}));
      setPendingAction(null);
      toast.success({
        title: 'Share token rotated',
        body: 'The old link stopped working. Send stakeholders the new share link.',
      });
      await packRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Rotate failed', body: error.message });
    }
  }

  if (packRes.loading) return <LoadingSkeleton rows={5} />;
  if (packRes.error) return <ErrorState error={packRes.error} />;
  if (!pack) return <EmptyState title="Status pack not found" />;

  const isPublished = pack.status === 'published';
  const expiryIso = expiryDateToIso(expiryDate);
  const expiryLabel = expiryIso ? new Date(expiryIso).toLocaleDateString() : 'no expiration';

  return (
    <>
      <nav className="briefing-detail-crumbs" aria-label="Breadcrumb">
        <Link to="/status-packs">Status Pack Library</Link>
        <span className="sep">›</span>
        <span>{pack.title || pack.id}</span>
      </nav>
      <PageHeader
        eyebrow={`STATUS PACK · ${(pack.scopeType || 'scope').toUpperCase()} · ${pack.scopeId || pack.id}`}
        title={pack.title || pack.id}
        subtitle={pack.summary || 'Sanitized stakeholder status pack.'}
        actions={<StatusChip status={pack.status || 'draft'} />}
      />

      <section className="bento" style={{ marginTop: 'var(--space-3)' }}>
        <article className="span-7 card">
          <div className="eyebrow">Approved public fields</div>
          <h2 className="card-title">Sanitized field set</h2>
          <FieldTable fields={pack.approvedFields || []} />
        </article>
        <article className="span-5 card">
          <div className="eyebrow">Publish controls</div>
          <h2 className="card-title">Read-only share posture</h2>
          <dl className="briefing-provenance">
            <div>
              <dt>Share token</dt>
              <dd>{pack.shareId || 'Draft only'}</dd>
            </div>
            <div>
              <dt>Published</dt>
              <dd>{pack.publishedAt ? fmtDate(pack.publishedAt) : 'Not published'}</dd>
            </div>
            <div>
              <dt>Expiration</dt>
              <dd>{fmtDate(pack.shareExpiresAt)}</dd>
            </div>
            <div>
              <dt>Mutations</dt>
              <dd>{pack.mutationsAllowed ? 'Enabled' : 'Disabled'}</dd>
            </div>
          </dl>
          {!isPublished ? (
            <label className="form-field" style={{ marginTop: 16 }}>
              <span className="label">Share expiration</span>
              {/* FS-SHARE-2: expiry is a date picker, editable before publish. */}
              <input
                className="input"
                type="date"
                value={expiryDate}
                onChange={(event) => setExpiryDate(event.target.value)}
              />
            </label>
          ) : null}
          <div className="row" style={{ marginTop: 16, gap: 10, flexWrap: 'wrap' }}>
            {!isPublished ? (
              <button
                className="btn primary sm tactile-press"
                type="button"
                onClick={() => setPendingAction('publish')}
                disabled={publishMut.running}
              >
                {publishMut.running ? 'Publishing…' : 'Publish read-only share'}
              </button>
            ) : (
              <>
                <button
                  className="btn danger sm"
                  type="button"
                  onClick={() => setPendingAction('revoke')}
                  disabled={publishMut.running}
                >
                  Revoke share
                </button>
                <button
                  className="btn outline sm"
                  type="button"
                  onClick={() => setPendingAction('rotate')}
                  disabled={publishMut.running}
                >
                  Rotate share token
                </button>
              </>
            )}
            {pack.shareId && isPublished ? (
              <Link className="btn outline sm" to={`/status/${pack.shareId}`}>
                Open published view
              </Link>
            ) : null}
          </div>
        </article>
        <article className="span-12 card">
          <div className="eyebrow">Approved artifacts</div>
          <h2 className="card-title">Stakeholder-safe evidence set</h2>
          <ArtifactList artifacts={pack.approvedArtifacts || []} />
        </article>
      </section>

      {/* AC-SHARE-2.1: publish confirms with the audience + expiry named;
          revoke confirms with typed feedback; rotate confirms the old link dies. */}
      <ConfirmActionDialog
        open={pendingAction === 'publish'}
        title="Publish this read-only share?"
        body={`Anyone with the share link will be able to view this sanitized ${pack.scopeType || 'scope'} status pack until ${expiryLabel}. Only the approved public fields and artifacts are exposed, and publishing is recorded with your name in the audit log.`}
        confirmLabel="Publish share"
        busy={publishMut.running}
        onConfirm={() => publishPack()}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmActionDialog
        open={pendingAction === 'revoke'}
        title="Revoke this stakeholder share?"
        body="The public link stops resolving immediately and stakeholders see a revocation notice. This is recorded with your name in the audit log."
        confirmLabel="Revoke share"
        tone="danger"
        requireNote
        noteLabel="Revocation feedback"
        notePlaceholder="Why is this share being revoked?"
        busy={publishMut.running}
        onConfirm={(note) => revokePack(note)}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmActionDialog
        open={pendingAction === 'rotate'}
        title="Rotate the share token?"
        body="The current share link stops working immediately and a new token is issued. Stakeholders must be re-sent the new link."
        confirmLabel="Rotate token"
        busy={publishMut.running}
        onConfirm={() => rotateShare()}
        onCancel={() => setPendingAction(null)}
      />
    </>
  );
}

// Shared portal chrome so every state of the public share (loading, expired /
// revoked / not-found, error, and the published pack) is presented inside the
// same branded, centered frame instead of a bare edge-to-edge block.
function PublicStatusShell({ children }) {
  return (
    <main
      style={{
        minHeight: '100vh',
        padding: '32px 24px 56px',
        background:
          'radial-gradient(circle at top left, rgba(11,90,155,0.12), transparent 36%), linear-gradient(180deg, #f5f8fb 0%, #eef3f8 100%)',
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>{children}</div>
    </main>
  );
}

export function PublicStatusPage() {
  const { shareId } = useParams();
  const statusRes = useApi(`/public-status/${shareId}`);
  const pack = statusRes.data?.data || null;

  if (statusRes.loading) {
    return (
      <PublicStatusShell>
        <LoadingSkeleton rows={5} />
      </PublicStatusShell>
    );
  }
  if (statusRes.error) {
    // The server explains unavailable shares (expired / revoked / never
    // published) — surface that reason instead of a generic failure (TT-10).
    if (statusRes.error.status === 404) {
      return (
        <PublicStatusShell>
          <EmptyState
            title="This status link is no longer available"
            description={statusRes.error.message}
          />
        </PublicStatusShell>
      );
    }
    return (
      <PublicStatusShell>
        <ErrorState error={statusRes.error} />
      </PublicStatusShell>
    );
  }
  if (!pack) {
    return (
      <PublicStatusShell>
        <EmptyState title="Public status not found" />
      </PublicStatusShell>
    );
  }

  return (
    <PublicStatusShell>
      <PageHeader
          eyebrow="Read-only Status Portal"
          title={pack.title || 'Published Status Pack'}
          subtitle={pack.summary || 'Approved stakeholder status view.'}
          actions={<StatusChip status={pack.status || 'unknown'} />}
        />

        <section className="bento" style={{ marginTop: 'var(--space-3)' }}>
          <article className="span-8 card">
            <div className="eyebrow">Approved public fields</div>
            <h2 className="card-title">Read-only published view</h2>
            <FieldTable fields={pack.approvedFields || []} />
          </article>
          <article className="span-4 card">
            <div className="eyebrow">Share controls</div>
            <h2 className="card-title">Portal posture</h2>
            <dl className="briefing-provenance">
              <div>
                <dt>Share token</dt>
                <dd>{pack.shareId}</dd>
              </div>
              <div>
                <dt>Published</dt>
                <dd>{pack.publishedAt ? fmtDate(pack.publishedAt) : '—'}</dd>
              </div>
              <div>
                <dt>Expiration</dt>
                <dd>{fmtDate(pack.shareExpiresAt)}</dd>
              </div>
              <div>
                <dt>Mutations</dt>
                <dd>{pack.mutationsAllowed ? 'Enabled' : 'Disabled'}</dd>
              </div>
            </dl>
            <p className="muted small" style={{ marginTop: 14 }}>
              This portal exposes approved, sanitized data only. No mutation endpoints are available
              from this view.
            </p>
          </article>
          <article className="span-12 card">
            <div className="eyebrow">Approved artifacts</div>
            <h2 className="card-title">Stakeholder-safe evidence references</h2>
            <ArtifactList artifacts={pack.approvedArtifacts || []} />
          </article>
        </section>
    </PublicStatusShell>
  );
}
